import { ReferenceItemViewModel } from "@/models/ViewModels/ReferenceModel";
import moment from "moment";
import store from "@/store";
import IResponseModel, { ApiException } from "@/api/ResponseModel";
import SchoolAddress from "@/models/APIModels/SchoolAddress";
import _ from "lodash";
import {
  AdministrationPayerTypeEnum,
  AdministratorRoleEnum,
  DonorRoleEnum,
  FundingRequestStatusEnum,
  SchoolOfficerRoleEnum,
  UserCategoryEnum,
} from "./ModelEnums";
import { FundingRequestRecipientViewModel } from "@/models/ViewModels/FundingRequestModel";
import { UserSecurityDetail } from "@/models/APIModels/UserSecurityDetail";
import AdministrationPayerViewModel from "@/models/ViewModels/AdministrationPayerViewModel";

export const stringHasValue = (val?: string) => {
  if (val && val !== "") return val;
  else return undefined;
};

export const formatPhoneNumber = (phoneNumber: string) => {
  if (phoneNumber.length !== 10) return "Invalid Number";
  return `${phoneNumber.substring(0, 4)} ${phoneNumber.substring(
    4,
    7,
  )} ${phoneNumber.substring(7)}`;
};

export const mapReferenceCodeToName = (
  reference: ReferenceItemViewModel[],
  code: string,
) =>
  reference.find((item) => item.code.trim() === code)?.name ??
  "Unknown Reference";

export const formatDateTimeWithSeconds = (date?: Date) => {
  if (date) return moment(date).format("DD/MM/YYYY HH:mm:ss");
  else return "";
};

export const formatDateTime = (date?: Date) => {
  if (date) return moment(date).format("DD/MM/YYYY HH:mm");
  else return "";
};

export const formatDate = (date?: Date) => {
  if (date) return moment(date).format("DD/MM/YYYY");
  else return "";
};

/**
 *
 * This function on handle and process the responses received from the backend
 *
 * @param response  The response from the back end received
 * @param onContent The function that needs to be executed when there is content, like updating the UI values
 * @param onMessage The function that needs to be executed when there are messages, like showing the error popup
 *
 *
 */

export function processResponse<T>(
  response: IResponseModel<T>,
  onContent: (content: T) => void,
  onMessage: (apiException: ApiException) => void = handleMessage,
) {
  if (response.status == 200 || response.content)
    onContent(response.content as T);
  else {
    // console.log(response.exceptionType);
    // if (response.exceptionType)
    //   response.messages.forEach(
    //     (item) => (item.exceptionType = response.exceptionType)
    //   );
    onMessage(response);
  }
}

export function handleMessage(apiException: ApiException) {
  store.commit("mutStoreMessage", apiException);
}

export function formatAddress(address: SchoolAddress) {
  return `${address.street}, ${address.suburbOrCity}, ${address.australianStateCd}, ${address.postcode}`;
}

export function enableFundingRequestReview(
  fundingRequestStatus: FundingRequestStatusEnum,
): boolean {
  return (
    // Check funding Request Status and Permission for review

    (fundingRequestStatus === FundingRequestStatusEnum.FRS_APR ||
      fundingRequestStatus === FundingRequestStatusEnum.FRS_HRR) &&
    !!store.state.user?.userPermissions.some(
      (item) => item.userPermissionCd === "UP_FRPRj",
    )
  );
}

export function enableFundingRequestEdit(
  fundingRequestStatus: FundingRequestStatusEnum,
  userReference: string,
) {
  let user = store.state.user;
  return (
    /**
     * if you are admin, you can always edit
     * for status pincipal request review or HAF Request Revision
     * you can edit only if
     * * You are a principal
     * * The funding request requesting user is you
     * for status Draft you can edit only if
     * * The funding request requesting user is you
     */

    user?.userCategoryCd === UserCategoryEnum.UC_A ||
    ((fundingRequestStatus === FundingRequestStatusEnum.FRS_PRR ||
      fundingRequestStatus === FundingRequestStatusEnum.FRS_HRR) &&
      (user?.userRoleCd === "SOR_P" ||
        user?.userReference === userReference)) ||
    (fundingRequestStatus === FundingRequestStatusEnum.FRS_Dra &&
      user?.userReference === userReference)
  );
}

export function enableFundingRequestDelete(
  fundingRequestStatus: FundingRequestStatusEnum,
  requestingUserReference: string,
) {
  let user = store.state.user;
  return (
    /**
     * You can only delete funding request if
     * * the funding quest status is in Draft and
     * * you are either the principal, admin or the requesting user
     */
    fundingRequestStatus === FundingRequestStatusEnum.FRS_Dra &&
    (user?.userRoleCd === "SOR_P" ||
      user?.userRoleCd === "SOR_A" ||
      user?.userReference === requestingUserReference)
  );
}

export function isObjectValueEqual<T>(obj1: T, obj2: T): boolean {
  return _.isEqual(obj1, obj2);
}

export function formatMonetaryAmt(amount?: number) {
  return amount ? `$${amount.toFixed(2)}` : "";
}

export function formatBankAccount(accountNumber?: string) {
  return accountNumber
    ? // ? accountNumber.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3")
      `XXX XXX ${accountNumber.substring(accountNumber.length - 3)}`
    : "Undefined";
}

export function formatRecipientNames(
  recipients: FundingRequestRecipientViewModel[],
) {
  return recipients
    .map(
      (item) =>
        `${item.givenName} ${item.familyName} - $${item.requestedFundingAmt}`,
    )
    .join(", ");
}

export function userRoleToName(userSecurityDetail?: UserSecurityDetail) {
  const userRole = userSecurityDetail?.userRoleCd || "";

  switch (userRole.trim()) {
    case AdministratorRoleEnum.AR_Y:
      return "System Administrator";
    case AdministratorRoleEnum.AR_S:
      return "State Administrator";
    case AdministratorRoleEnum.AR_H:
      return "Head Administrator";
    case SchoolOfficerRoleEnum.SOR_P:
      return "School Principal";
    case SchoolOfficerRoleEnum.SOR_A:
      return "School HAF Admin";
    case SchoolOfficerRoleEnum.SOR_S:
      return "School Staff";
    case SchoolOfficerRoleEnum.SOR_O:
      return "Other";
    case DonorRoleEnum.DT_P:
    case DonorRoleEnum.DT_B:
      return "Donor";

    default:
      return "Unknown Role";
  }
}

export function getAdministrationPayer(
  administrationPayerTypeCd: AdministrationPayerTypeEnum,
) {
  const administratorPayer = new AdministrationPayerViewModel();
  administratorPayer.administrationPayerTypeCd = administrationPayerTypeCd;
  return administratorPayer;
}

export function getTransactionPaymentTableColumns(): {
  name: string;
  label: string;
  field: string;
  align?: "left" | "right" | "center";
  format?: (val: any) => string;
}[] {
  return [
    { name: "fullName", label: "Full Name", field: "fullName", align: "left" },
    {
      name: "chargeDate",
      label: "Charge Date",
      field: "chargeDT",
      align: "left",
      format: (val: Date) => (val ? formatDate(val) : ""),
    },
    {
      name: "paymentDate",
      label: "Payment Date",
      field: "paymentDt",
      align: "left",
      format: (val: Date) => (val ? formatDate(val) : ""),
    },
    {
      name: "expectedPaymentAmount",
      label: "Expected Payment Amount",
      field: "expectedPaymentAmt",
      align: "right",
      format: (val: number) =>
        val ? String(val.toFixed(2)).replace(/^-?/, "$&\$") : "",
    },
    {
      name: "transactionDate",
      label: "Transaction Date",
      field: "transactionDT",
      align: "left",
      format: (val: Date) => (val ? formatDate(val) : ""),
    },
    {
      name: "actualPaymentAmount",
      label: "Actual Payment Amount",
      field: "actualPaymentAmt",
      align: "right",
      format: (val: number) =>
        val ? String(val.toFixed(2)).replace(/^-?/, "$&\$") : "",
    },
    {
      name: "paymentType",
      label: "Payment Type",
      field: "paymentTypeCd",
      align: "left",
      format: (val: string) => {
        return val
          ? mapReferenceCodeToName(store.state.referenceData.paymentTypes, val)
          : "";
      },
    },
    {
      name: "paymentStatus",
      label: "Payment Status",
      field: "paymentStatusCd",
      align: "left",
      format: (val: string) => {
        return val
          ? mapReferenceCodeToName(
              store.state.referenceData.paymentStatuses,
              val,
            )
          : "";
      },
    },
  ];
}
